import { getPrimaryUnitCatalog } from "common/src/models/Experience";
import { uniq } from "lodash";
import {
    Coupon,
    CouponCode,
    CouponCodeStatus,
    CouponType,
    InFilter,
    Product,
    PurchaseRule,
    PurchaseRuleFilterObject,
    PurchaseRuleShortcutBXGYOption,
} from "types";
import { DemographicLineItem } from "../stores/createPurchaseStore";

/**
 * Product ids a coupon is restricted to, read from `purchaseRule.filter` only.
 */
export const getCouponProductRestrictions = (coupon?: Coupon): ID[] => {
    const filter = coupon?.purchaseRule?.filter;
    if (filter?.object !== PurchaseRuleFilterObject.AND_FILTER || !filter.filters) {
        return [];
    }

    const productFilter = filter.filters.find(
        (item): item is InFilter => item.object === PurchaseRuleFilterObject.IN_FILTER && item.field === "product.id",
    );

    return productFilter?.operand?.filter((id): id is ID => id != null) ?? [];
};

export const getCodes = (coupons: Coupon[], codes: CouponCode[]) => {
    const typeOfCodes = new Set([CouponType.BXGY, CouponType.DISCOUNT, CouponType.UNLOCK, CouponType.VOUCHER]);
    const couponIds = coupons
        .filter((coupon: Coupon) => typeOfCodes.has(coupon.type))
        .map((coupon: Coupon) => coupon.id);

    if (couponIds) {
        return codes.filter((code: CouponCode) => {
            return couponIds.includes(code?.coupon?.id) && code?.status && code.status < CouponCodeStatus.VOID;
        });
    }
};

/**
 * Checks given BXGY code can be applicable to the purchased primary units (demographics, or
 * equipment on an equipment-based experience)
 * 1. Check given BXGY code purchaseRule has any purchased unit templateId.
 * 2. If the templateId exists check that purchased unit quantity satisfies qualifying Quantity
 */
export const canApplyBXGYCode = (
    primaryUnitLineItems: Array<Pick<DemographicLineItem, "quantity" | "template">>,
    itemQuantity: number,
    purchaseRule: PurchaseRule,
    product: Product,
) => {
    const primaryUnitCatalog = getPrimaryUnitCatalog(product);
    const restrictedCodes = purchaseRule.actions.filter((action) => {
        const { templateIds, qualifyingQuantity, eligibleQuantity } = action.shortcut
            ?.options as PurchaseRuleShortcutBXGYOption;
        const qualifyingPurchaseQuantity = qualifyingQuantity + (eligibleQuantity ?? 0);
        let qualifyingPrice = Number.MAX_SAFE_INTEGER;
        let eligiblePrice = Number.MAX_SAFE_INTEGER;
        let qualifyingUnitsBooked = 0;

        if (templateIds?.length === 0) {
            // Applies to all purchasable items (demographics or equipment)
            return itemQuantity >= qualifyingPurchaseQuantity;
        }

        const eligibleUnits = primaryUnitLineItems.filter((lineItem) => {
            const catalogItem = primaryUnitCatalog.find((catalog) => catalog.template?.id === lineItem.template.id);
            const unitPrice = catalogItem?.prices?.price?.min;
            if (unitPrice) {
                if (templateIds?.includes(lineItem.template.id)) {
                    qualifyingUnitsBooked += lineItem.quantity;
                    qualifyingPrice = Math.min(qualifyingPrice, unitPrice);
                } else {
                    // Any purchased unit with less price that we can apply code
                    eligiblePrice = Math.min(eligiblePrice, unitPrice);
                }

                // if any qualifying unit has purchased more than qualifying quantity
                const qualifiedUnit = unitPrice <= qualifyingPrice && qualifyingUnitsBooked > qualifyingQuantity;

                // if any purchased unit has price less than qualifying purchased Price
                const eligibleUnit = eligiblePrice <= qualifyingPrice && itemQuantity >= qualifyingPurchaseQuantity;

                return qualifiedUnit || eligibleUnit;
            }

            return false;
        });

        return eligibleUnits.length > 0;
    });

    return restrictedCodes.length > 0;
};

export const isStoreCredit = (coupon?: Partial<Coupon>) => coupon?.source === "store_credit";
export const isGift = (coupon?: Partial<Coupon>) => coupon?.source === "gift";
export const isPaymentCoupon = (coupon?: Partial<Coupon>) => isStoreCredit(coupon) || isGift(coupon);
export const isVoucherCoupon = (coupon?: Partial<Coupon>) => coupon?.type === CouponType.VOUCHER;

export const getUniqueCodes = (codes: string) => {
    // Remove all white-space characters from the input
    const cleanCodes = codes
        .replace(/[ \t]+/g, "") // Remove tabs and spaces
        .replace(/,\s*/g, "\n") // Replace commas followed by optional spaces with a newline
        .replace(/\n+/g, "\n") // Replace multiple newlines with a single newline
        .trim()
        .toUpperCase();

    return uniq(cleanCodes.split("\n")); // Remove code duplicates
};
